import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Edit, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PhotoThumb } from '@/components/PhotoThumb';
import { useToast } from '@/hooks/use-toast';
import { adoptionApi } from '../api';
import type { AdopterProfile, AdoptionFormTemplate, FormField } from '../types';
import { animalsApi } from '@/features/animals/api';
import { useLookupsSnapshot, type LookupItem, toOptionLabel } from '@/features/lookups/snapshot';
import { patchTemplate } from '../utils/patchTemplate';
import { formatAnimalAge } from '@/lib/animalAge';

export function ConfirmProfilePage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { toast } = useToast();

    const state = location.state as { animalId?: number; shelterId?: number } | null;
    const animalId = toNumber(state?.animalId) || toNumber(searchParams.get('animalId'));
    const shelterId = toNumber(state?.shelterId) || toNumber(searchParams.get('shelterId'));

    const [profile, setProfile] = useState<AdopterProfile | null>(null);
    const [template, setTemplate] = useState<AdoptionFormTemplate | null>(null);
    const [animal, setAnimal] = useState<any | null>(null);
    const [formResponse, setFormResponse] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: lookups } = useLookupsSnapshot();
    const contactInfo = useMemo(() => profile ? mapContactInfo(profile, lookups?.cities) : null, [profile, lookups]);
    const templateFromResponse = (formResponse?.template || formResponse?.formTemplate) as AdoptionFormTemplate | undefined;
    const effectiveTemplate = templateFromResponse || template || null;
    const formValues = useMemo(() => {
        if (!formResponse?.answersJson) return {};
        return formResponse.answersJson || {};
    }, [formResponse]);
    const answersContactInfo = useMemo(
        () => mapContactInfoFromAnswers(formValues, effectiveTemplate),
        [formValues, effectiveTemplate]
    );
    const finalContactInfo = formResponse ? answersContactInfo || undefined : contactInfo;

    useEffect(() => {
        let active = true;

        const loadProfile = async () => {
            try {
                const [profileData] = await Promise.all([
                    adoptionApi.getMyProfile(),
                ]);

                if (!active) return;

                if (!profileData) {
                    navigate('/adoption/wizard', { state: { animalId, shelterId } });
                    return;
                }

                setProfile(profileData);

                let animalData: any | null = null;
                if (animalId) {
                    try {
                        animalData = await animalsApi.getById(animalId);
                    } catch (error) {
                        console.error('Error loading animal data:', error);
                    }
                }

                if (!active) return;
                setAnimal(animalData);

                const effectiveShelterId =
                    toNumber(shelterId) ||
                    toNumber(animalData?.shelterId ?? animalData?.shelter_id) ||
                    toNumber(profileData.shelterId ?? profileData.shelter_id);

                const templateData = await adoptionApi.getActiveTemplate(effectiveShelterId || undefined);
                if (!active) return;
                setTemplate(patchTemplate(templateData));

                try {
                    const profileId = (profileData as any)?.id;
                    if (!profileId) {
                        navigate('/adoption/wizard', { state: { animalId, shelterId } });
                        return;
                    }
                    const applications = await adoptionApi.listApplications({
                        filter: { where: { adopterProfileId: Number(profileId) }, order: ['createdAt DESC'], limit: 1 },
                    });
                    if (!active) return;
                    if (Array.isArray(applications) && applications.length > 0) {
                        const latest = applications[0];
                        if (latest?.id) {
                            const full = await adoptionApi.getApplicationById(latest.id);
                            if (!active) return;
                            const response =
                                (full as any).formResponse ||
                                (full as any).adoptionFormResponse ||
                                (full as any).form_response ||
                                null;
                            setFormResponse(response);
                            if (!response) {
                                navigate('/adoption/wizard', { state: { animalId, shelterId } });
                            }
                        }
                    } else {
                        navigate('/adoption/wizard', { state: { animalId, shelterId } });
                    }
                } catch (error) {
                    console.error('Error loading latest form response:', error);
                    if (active) setFormResponse(null);
                }
            } catch (error) {
                console.error('Error loading profile:', error);
                navigate('/adoption/wizard', { state: { animalId, shelterId } });
            } finally {
                if (active) setIsLoading(false);
            }
        };
        loadProfile();

        return () => {
            active = false;
        };
    }, [navigate, animalId, shelterId]);

    const handleConfirm = async () => {
        if (!profile || !animalId) return;

        try {
            setIsSubmitting(true);
            const effectiveShelterId =
                toNumber(shelterId) ||
                toNumber(animal?.shelterId ?? animal?.shelter_id) ||
                toNumber(profile.shelterId ?? profile.shelter_id) ||
                1;
            await adoptionApi.createApplication({
                adopterProfileId: profile.id,
                animalId: animalId,
                shelterId: effectiveShelterId,
                notes: "Solicitud confirmada desde perfil existente",
            });

            toast({
                title: "¡Solicitud enviada!",
                description: "Tu solicitud ha sido registrada exitosamente.",
                variant: "default",
            });

            navigate('/adopter/dashboard');
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error.response?.data?.message || "No se pudo crear la solicitud.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!profile) return null;

    const photoUrl = resolvePhotoUrl(animal?.photos?.[0]?.url);
    const animalName = animal?.name || '—';
    const animalBreed = resolveAnimalLookup(lookups?.breeds, animal?.breedId ?? animal?.breed_id, animal?.breedLabel ?? animal?.breed_label);
    const animalColor = resolveAnimalLookup(lookups?.colors, animal?.colorId ?? animal?.color_id, animal?.colorLabel ?? animal?.color_label);
    const animalSex = resolveAnimalLookup(lookups?.sexes, animal?.sexId ?? animal?.sex_id, animal?.sexLabel ?? animal?.sex_label);
    const animalAge = formatAnimalAge({
        birthDate: animal?.birth_date ?? animal?.birthDate,
        approxAgeMonths: animal?.approxAgeMonths ?? animal?.approx_age_months,
    });
    return (
        <div className="container max-w-2xl py-12">
            <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Volver
            </Button>

            <Card>
                <CardHeader>
                    <CardTitle>Confirmar Solicitud de Adopción</CardTitle>
                    <CardDescription>
                        Revisa que tus datos estén actualizados antes de enviar la solicitud.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {animal && (
                        <div className="rounded-lg border p-4 space-y-4 bg-muted/40">
                            <div className="flex flex-col sm:flex-row gap-4">
                                {photoUrl && (
                                    <PhotoThumb
                                        src={photoUrl}
                                        alt={animalName}
                                        className="h-28 w-28 rounded-lg"
                                    />
                                )}
                                <div className="flex-1">
                                    <p className="text-xs uppercase text-muted-foreground">Animal seleccionado</p>
                                    <h3 className="text-xl font-semibold">{animalName}</h3>
                                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="font-medium text-muted-foreground">Raza:</span>
                                            <p>{animalBreed || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-muted-foreground">Color:</span>
                                            <p>{animalColor || '—'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-muted-foreground">Edad:</span>
                                            <p>{animalAge !== '-' ? animalAge : '—'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-muted-foreground">Sexo:</span>
                                            <p>{animalSex || '—'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="rounded-lg border p-4 space-y-3 bg-muted/50">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-muted-foreground">Nombre:</span>
                                <p>{finalContactInfo?.name || '—'}</p>
                            </div>
                            <div>
                                <span className="font-medium text-muted-foreground">Email:</span>
                                <p>{finalContactInfo?.email || '—'}</p>
                            </div>
                            <div>
                                <span className="font-medium text-muted-foreground">Teléfono:</span>
                                <p>{finalContactInfo?.phone || '—'}</p>
                            </div>
                            <div>
                                <span className="font-medium text-muted-foreground">Ciudad:</span>
                                <p>{finalContactInfo?.city || '—'}</p>
                            </div>
                        </div>
                    </div>

                    {effectiveTemplate && formResponse?.answersJson && (
                        <div className="space-y-4">
                            {effectiveTemplate.schemaJson.sections.map((section) => (
                                <div key={section.id} className="rounded-lg border p-4 space-y-3">
                                    <h3 className="text-base font-semibold">
                                        {section.label || section.title || section.id}
                                    </h3>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {section.fields
                                            .filter((field) => isFieldVisible(field, formValues))
                                            .map((field) => (
                                                <div key={field.id} className="text-sm">
                                                    <p className="text-muted-foreground">{field.label}</p>
                                                    <p className="font-medium">{renderValue(field, formValues, lookups)}</p>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <Button
                            className="w-full bg-green-600 hover:bg-green-700"
                            size="lg"
                            onClick={handleConfirm}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                            )}
                            Confirmar y Enviar Solicitud
                        </Button>

                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => navigate('/adoption/wizard', { state: { animalId, shelterId } })}
                            disabled={isSubmitting}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            Editar mis datos primero
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

interface ContactInfo {
    name?: string;
    email?: string;
    phone?: string;
    city?: string;
}

function mapContactInfo(profile: AdopterProfile, cities?: LookupItem[] | null): ContactInfo {
    const name = resolveName(profile);
    const email = resolveEmail(profile);
    const phone = resolvePhone(profile);
    const city = resolveCity(profile, cities || undefined);

    return { name, email, phone, city };
}

function resolveName(profile: AdopterProfile): string | undefined {
    const direct = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
    if (direct) return direct;

    const user = profile.user as any;
    if (user) {
        const userFull = user.fullName || user.full_name;
        if (userFull) return String(userFull);
        const composed = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        if (composed) return composed;
    }
    return undefined;
}

function resolveEmail(profile: AdopterProfile): string | undefined {
    return profile.email || (profile.user as any)?.email || undefined;
}

function resolvePhone(profile: AdopterProfile): string | undefined {
    return profile.phone || (profile.user as any)?.phone || undefined;
}

function resolveCity(profile: AdopterProfile, cities?: LookupItem[]): string | undefined {
    const formatCityObject = (cityObj: any) => {
        if (!cityObj) return undefined;
        const label = cityObj.label || cityObj.name || cityObj.city || cityObj.title;
        const department = cityObj.department || cityObj.state || cityObj.region;
        if (!label) return undefined;
        return department ? `${label} - ${department}` : label;
    };

    const rawCity = profile.city;
    if (typeof rawCity === 'object' && rawCity !== null) {
        const formatted = formatCityObject(rawCity);
        if (formatted) return formatted;
    }

    if (profile.cityLabel) return profile.cityLabel || undefined;
    if (profile.cityName) return profile.cityName || undefined;

    if (typeof rawCity === 'string') {
        const trimmed = rawCity.trim();
        if (trimmed && /[a-zA-Z]/.test(trimmed)) {
            return trimmed;
        }
        const cleaned = trimmed.replace(/city[_\s-]*id\s*=?\s*/i, '');
        if (cleaned && /[a-zA-Z]/.test(cleaned)) {
            return cleaned;
        }
    }

    const cityId = resolveCityId(profile);
    if (cityId && cities) {
        const fromLookup = cities.find((c) => Number(c.id) === cityId);
        if (fromLookup) return formatCityObject(fromLookup);
    }

    if (typeof rawCity === 'number') return String(rawCity);
    if (typeof rawCity === 'string' && rawCity.trim()) {
        return rawCity.trim();
    }

    return undefined;
}

function isFieldVisible(field: FormField, values: Record<string, any>) {
    if (!field.conditional) return true;
    const dependentValue = values[field.conditional.field];
    const { operator, value } = field.conditional;
    switch (operator) {
        case '==': return dependentValue === value;
        case '!=': return dependentValue !== value;
        case '>': return dependentValue > value;
        case '<': return dependentValue < value;
        case 'in': return Array.isArray(value) && value.includes(dependentValue);
        case 'contains': return Array.isArray(dependentValue) && dependentValue.includes(value);
        default: return true;
    }
}

function renderValue(field: FormField, values: Record<string, any>, lookups?: ReturnType<typeof useLookupsSnapshot>['data']) {
    const raw = values[field.id];
    if (raw === undefined || raw === null || raw === '') return 'Sin responder';
    if (Array.isArray(raw)) return raw.join(' - ');
    if (typeof raw === 'boolean') return raw ? 'Sí' : 'No';

    const match = field.options?.find((opt: any) => String(opt.value) === String(raw));
    if (match) return match.label;

    if (field.type === 'city') {
        const label = resolveCityLabel(raw, lookups?.cities);
        if (label) return label;
    }

    if (field.lookupType) {
        const lookupLabel = resolveLookupLabel(field.lookupType, raw, lookups);
        if (lookupLabel) return lookupLabel;
    }

    if (field.type === 'radio' && (raw === 'true' || raw === 'false')) {
        return raw === 'true' ? 'Sí' : 'No';
    }

    return String(raw);
}

function mapContactInfoFromAnswers(values: Record<string, any>, template: AdoptionFormTemplate | null) {
    if (!values || !template) return null;

    const candidates = {
        name: ['name', 'full_name', 'fullName', 'nombre', 'nombre_completo'],
        email: ['email', 'correo', 'correo_electronico'],
        phone: ['phone', 'telefono', 'celular', 'mobile'],
        city: ['city', 'ciudad', 'municipio'],
    };

    const byId = (keys: string[]) => {
        for (const key of keys) {
            const value = values[key];
            if (value !== undefined && value !== null && value !== '') return String(value);
        }
        return undefined;
    };

    const byLabel = (keys: string[]) => {
        for (const section of template.schemaJson.sections) {
            for (const field of section.fields) {
                const label = (field.label || '').toLowerCase();
                if (!label) continue;
                if (keys.some((k) => label.includes(k.replace(/_/g, ' ')))) {
                    const value = values[field.id];
                    if (value !== undefined && value !== null && value !== '') return String(value);
                }
            }
        }
        return undefined;
    };

    const name = byId(candidates.name) || byLabel(['nombre', 'name']);
    const email = byId(candidates.email) || byLabel(['correo', 'email']);
    const phone = byId(candidates.phone) || byLabel(['teléfono', 'telefono', 'celular', 'phone']);
    const city = byId(candidates.city) || byLabel(['ciudad', 'municipio', 'city']);

    if (!name && !email && !phone && !city) return null;
    return { name, email, phone, city };
}

function resolveLookupLabel(
    lookupType: string | undefined,
    raw: any,
    lookups?: ReturnType<typeof useLookupsSnapshot>['data']
): string | undefined {
    if (!lookupType || !lookups) return undefined;
    const id = Number(raw);
    const collection = (() => {
        switch (lookupType) {
            case 'species': return lookups.species;
            case 'sex': return lookups.sexes;
            case 'size': return lookups.sizes;
            case 'breed': return lookups.breeds;
            case 'color': return lookups.colors;
            case 'status': return lookups.statuses;
            default: return undefined;
        }
    })();
    const item = collection?.find((i: any) => Number(i.id) === id);
    return item ? toOptionLabel(item) : undefined;
}

function resolveCityLabel(raw: any, cities?: LookupItem[]) {
    if (typeof raw === 'string' && /[a-zA-Z]/.test(raw)) return raw;
    const id = Number(raw);
    if (!Number.isNaN(id) && cities) {
        const city = cities.find((c) => Number(c.id) === id);
        if (city) return toOptionLabel(city);
    }
    return undefined;
}

function resolveAnimalLookup(collection: LookupItem[] | undefined, id: any, fallback?: string) {
    if (fallback) return fallback;
    const parsed = Number(id);
    if (!Number.isNaN(parsed) && collection) {
        const item = collection.find((entry) => Number(entry.id) === parsed);
        if (item) return toOptionLabel(item);
    }
    return undefined;
}

function resolvePhotoUrl(url?: string) {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    return `${import.meta.env.VITE_API_URL}${url}`;
}

function toNumber(value: any): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function resolveCityId(profile: AdopterProfile): number | undefined {
    const candidates = [
        profile.cityId,
        profile.city_id,
        (profile.city as any)?.id,
        typeof profile.city === 'string' ? extractNumericId(profile.city) : undefined,
    ];

    for (const candidate of candidates) {
        const parsed = parseNumeric(candidate);
        if (parsed !== undefined) return parsed;
    }
    return undefined;
}

function parseNumeric(value: any): number | undefined {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const num = Number(value.trim());
        if (!Number.isNaN(num)) return num;
    }
    return undefined;
}

function extractNumericId(value: string): number | undefined {
    const match = value.match(/(\d+)/);
    if (match) {
        return Number(match[1]);
    }
    return undefined;
}
